import {
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import useFetch from "../hooks/useFetch";
import * as XLSX from "xlsx";
import CustomModal from "../components/ui/CustomModal";
import CustomTextField from "../components/ui/CustomTextField";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IconButton } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import theme from "../components/ui/theme";
import axios from "axios";

export default function Delivery() {
  const fetchData = useFetch();
  const isSmallScreen = useMediaQuery(theme.breakpoints.only("xs"));

  const [data, setData] = useState([]);
  const [openNotificationModal, setOpenNotificaitonModal] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [notificationData, setNotificationData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const filtered = data.filter(
      (item) =>
        item.replenishmentPcs !== undefined &&
        item.replenishmentPcs !== null &&
        item.replenishmentPcs !== 0,
    );
    setFilteredItems(filtered);
  }, [data]);

  const getData = async () => {
    let response = await fetchData("/api/stock/dashboard");
    response = response.filter((i) => i.replenishNotification);
    if (response) setData(response);
  };

  const handleRowUpdate = (newRow, oldRow) => {
    const updatedRow = { ...newRow };
    updatedRow.replenishmentPcs =
      updatedRow.replenishmentCartons * updatedRow.unitsPerCarton;
    setData((prev) =>
      prev.map((row) => (row._id === updatedRow._id ? updatedRow : row)),
    );
    return updatedRow;
  };

  const columns = [
    {
      field: "ean",
      headerName: "EAN",
      width: 140,
      renderCell: (params) => (
        <Box>
          {params.row.ean}
          <IconButton
            size={"small"}
            onClick={() =>
              window.open(
                `https://partner.bol.com/sdd/assortment-new/product/${params.row.productId}/sales-insight/`,
                "_blank",
              )
            }
          >
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ),
    },
    {
      field: "referenceCode",
      headerName: "SKU",
      width: isSmallScreen ? undefined : 150,
      flex: isSmallScreen ? 1 : undefined,
    },
    {
      field: "picqerStock",
      headerName: "Magazijn",
      type: "number",
      width: 100,
    },
    {
      field: "fbbStock",
      headerName: "Lvb",
      type: "number",
      width: 100,
    },
    {
      field: "unitsPerCarton",
      headerName: "Stuks in doos",
      type: "number",
      width: 125,
    },
    {
      field: "sales",
      headerName: "Verkopen",
      type: "number",
      flex: 1,
    },

    {
      field: "fbbStockDaysSales",
      headerName: "Voorraaddagen",
      type: "number",
      width: 125,
      renderCell: (params) => {
        const value = params.row.saleAnalysis?.fbbStockDays ?? 0;
        const min = Number(process.env.REACT_APP_MINIMUM_FBB_STOCKDAYS);
        const sales = params.row.sales;

        return (
          <>
            {value < min && sales > 0 ? "⚠️ " : ""}
            {value}
          </>
        );
      },
      valueGetter: (value, params) => {
        return params.saleAnalysis?.fbbStockDays ?? 0;
      },
    },
    {
      field: "forecast",
      headerName: "Prognose",
      type: "number",
      flex: 1,
    },
    {
      field: "fbbStockDaysForecast",
      headerName: "Voorraaddagen",
      type: "number",
      flex: 1,
      renderCell: (params) => {
        const value = params.row.forecastAnalysis?.fbbStockDays ?? 0;
        const min = Number(process.env.REACT_APP_MINIMUM_FBB_STOCKDAYS);
        const sales = params.row.sales;

        return (
          <>
            {value < min && sales > 0 ? "⚠️ " : ""}
            {value}
          </>
        );
      },
      valueGetter: (value, params) => {
        return params.forecastAnalysis?.fbbStockDays ?? 0;
      },
    },
    {
      field: "stockCartonsPcs",
      headerName: "Opslagdozen",
      type: "number",
      renderCell: (params) => {
        return (
          <>
            {Number(
              (params.row.picqerStock / params.row.unitsPerCarton).toFixed(1),
            )}
          </>
        );
      },
      valueGetter: (value, params) => {
        return params.picqerStock / params.unitsPerCarton;
      },
    },
    {
      field: "replenishmentCartons",
      headerName: "Dozen aanvullen",
      type: "number",
      flex: 1,
      editable: true,
    },
    {
      field: "replenishmentPcs",
      headerName: "Stuks aanvullen",
      type: "number",
      flex: 1,
      renderCell: (params) => {
        const inProgressStock = params.row.fbbInProgress;
        const valid =
          params.row.replenishmentCartons <=
          params.row.picqerStock / params.row.unitsPerCarton;
        const amount =
          params.row.unitsPerCarton * params.row.replenishmentCartons || "";

        const value = amount > 0 ? amount : "";
        return (
          <>
            {amount > 0 && (
              <Chip
                label={!valid ? `⚠️ ${value}` : value}
                size="small"
                variant={"outlined"}
              />
            )}
            {inProgressStock > 0 && (
              <Chip
                label={`${inProgressStock}`}
                size="small"
                variant={"outlined"}
                color={"info"}
                sx={{ ml: 1 }}
              />
            )}
          </>
        );
      },
    },
    {
      field: "afterReplenishment",
      headerName: "Na aanvullen",
      type: "number",
      width: 150,
      renderCell: (params) => {
        const inProgressStock = params.row.fbbInProgress ?? 0;

        const fbbStock = params.row.fbbStock ?? 0;
        const replenishmentPcs = params.row.replenishmentPcs ?? 0;
        const sales = params.row.sales ?? 0;
        const min = Number(process.env.REACT_APP_MINIMUM_FBB_STOCKDAYS);

        const saleStockDays =
          sales > 0
            ? Math.floor(
                (fbbStock + replenishmentPcs + inProgressStock) / (sales / 28),
              )
            : 0;

        const showWarning = saleStockDays < min;

        if (sales === 0) {
          return <Chip label="Geen verkopen" size="small" />;
        }

        return (
          <Chip
            label={`${showWarning ? "⚠️ " : ""}${saleStockDays} dagen`}
            size="small"
            color={inProgressStock > 0 ? "info" : ""}
          />
        );
      },
      valueGetter: (value, params) => {
        const inProgressStock = params.fbbInProgress;
        const fbbStock = params.fbbStock ?? 0;
        const sales = params.sales ?? 0;

        const saleStockDays =
          sales > 0
            ? Math.floor((fbbStock + inProgressStock) / (sales / 28))
            : 0;

        if (params.sales === 0) return 999;

        return saleStockDays;
      },
    },
  ];

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, []);

  function handleExport() {
    const output = filteredItems
      .map((item) => `${item.ean},${item.replenishmentPcs}`)
      .join("\n");

    navigator.clipboard.writeText(output).then(() => {
      console.log("Gekopieerd naar clipboard");
    });
  }

  async function handleMakeOrder() {
    const excelData = filteredItems.map((item) => ({
      EAN: item.ean,
      Aantal: item.replenishmentPcs,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Aanvuladvies");

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `Picqer aanvuladvies - ${timestamp}.xlsx`;

    XLSX.writeFile(workbook, filename);
  }

  const fields = [
    {
      label: "Bestelnummer",
      name: "orderId",
    },
    {
      label: "Referentie",
      name: "reference",
    },
    {
      label: "Afleverdatum",
      name: "deliveryDate",
    },
    {
      label: "Ladingdrager",
      name: "carrier",
    },
  ];

  const lines = [
    "Beste,",
    "",
    "Ik heb zojuist een LVB zending aangemaakt.",
    "De relevante informatie vind je hieronder.",
    "",
    "Betreft LVB Zending",
    `Bestelnummer: ${notificationData.orderId || ""}`,
    `Referentie: ${notificationData.reference || ""}`,
    `Afleverdatum: ${notificationData.deliveryDate || ""}`,
    `Ladingdrager: ${notificationData.carrier || ""}`,
    "",
    "Picklijst en Verzendlabels in de bijlage via de mail",
    "",
    "Aantal dozen:",
    ...filteredItems.map(
      (item) =>
        `${item.ean} → ${item.replenishmentPcs} stuks → ${item.replenishmentCartons} ${item.replenishmentCartons === 1 ? "doos" : "dozen"}`,
    ),
    "",
    "Bedankt voor de verwerking!",
  ];

  function handleCopyAll() {
    const output = lines.join("\n");

    navigator.clipboard.writeText(output).then(() => {
      console.log("Alle gegevens gekopieerd naar clipboard!");
    });
  }

  function createMail() {
    const to = "info@efreightforwarding.com";
    const subject = `Bestelling ${notificationData.orderId} betreft LVB ZENDING (Leverdatum: ${notificationData.deliveryDate})`;
    const body = lines.join("\r\n");

    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    const mailtoLink = `mailto:${to}?subject=${encodedSubject}&body=${encodedBody}`;

    window.location.href = mailtoLink;
  }

  const getColumnVisibilityModel = () => {
    if (isSmallScreen) {
      return {
        ...columns.reduce((acc, col) => {
          acc[col.field] = false;
          return acc;
        }, {}),
        referenceCode: true,
        afterReplenishment: true,
      };
    }

    return columns.reduce((acc, col) => {
      acc[col.field] = true;
      return acc;
    }, {});
  };

  return (
    <Grid container rowSpacing={1}>
      {!isSmallScreen && (
        <Grid size={12}>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "end" }}>
            <Button
              variant={"contained"}
              onClick={() => setOpenNotificaitonModal(true)}
              sx={{ mr: 1 }}
              disabled={filteredItems.length === 0}
            >
              Notificatie aanmaken
            </Button>
            <CustomModal
              open={openNotificationModal}
              onClose={() => setOpenNotificaitonModal(false)}
              title={"Notificatie instellen"}
              handleSave={() => setOpenNotificaitonModal(false)}
            >
              <Box>
                <Grid container>
                  {fields.map((field, index) => (
                    <Grid size={{ xs: 12, sm: field.col }} key={index}>
                      <CustomTextField
                        field={field}
                        data={notificationData}
                        setData={setNotificationData}
                        errors={errors}
                        setErrors={setErrors}
                        editable={true}
                      />
                    </Grid>
                  ))}
                  <Grid sx={{ p: 1 }} size={10}>
                    <Box>
                      <Typography>Betreft LVB Zending</Typography>
                      <Typography>
                        Bestelnummer: {notificationData.orderId}
                      </Typography>
                      <Typography>
                        Referentie: {notificationData.reference}
                      </Typography>
                      <Typography>
                        Afleverdatum: {notificationData.deliveryDate}
                      </Typography>
                      <Typography>
                        Ladingdrager: {notificationData.carrier}
                      </Typography>
                      <Typography>
                        Picklijst en Verzendlabels via de mail
                      </Typography>
                      <Typography>Aantal dozen:</Typography>
                      {filteredItems.map((item, i) => {
                        return (
                          <Typography key={i}>
                            {item.ean} -> {item.replenishmentPcs} stuks ->{" "}
                            {item.replenishmentCartons}{" "}
                            {item.replenishmentCartons === 1 ? "doos" : "dozen"}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Grid>
                  <Grid size={2} sx={{ p: 1 }}>
                    <IconButton onClick={handleCopyAll} size="small">
                      <ContentCopyIcon />
                    </IconButton>
                    <IconButton
                      onClick={createMail}
                      size="small"
                      disabled={
                        !notificationData.orderId ||
                        !notificationData.deliveryDate
                      }
                    >
                      <MailOutlineIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            </CustomModal>
            <Button
              variant={"contained"}
              onClick={handleMakeOrder}
              sx={{ mr: 1 }}
              disabled={filteredItems.length === 0}
            >
              Download voor Picqer
            </Button>
            <Button
              variant={"contained"}
              onClick={handleExport}
              disabled={filteredItems.length === 0}
            >
              LVB
            </Button>
          </Box>
        </Grid>
      )}
      <Grid size={12} sx={{ height: "85vh" }}>
        {data.length > 0 ? (
          <DataGrid
            rows={data}
            columns={columns}
            getRowId={(row) => row._id}
            density="compact"
            processRowUpdate={handleRowUpdate}
            initialState={{
              columns: {
                columnVisibilityModel: getColumnVisibilityModel(),
              },
              sorting: {
                sortModel: [
                  {
                    field: "afterReplenishment",
                    sort: "asc",
                  },
                ],
              },
            }}
            sx={{
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
            }}
          />
        ) : (
          <LinearProgress />
        )}
      </Grid>
    </Grid>
  );
}
